import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { ArrowDown } from "components/icons/ArrowDown";
import { useEntityChatFilter } from "contexts/EntityChatFilterContext";
import { useChatFilterNudge } from "hooks/useChatFilterNudge";
import { useChatSeasons } from "hooks/useChatSeasons";
import { useUsEntities } from "hooks/useUsEntities";
import { ChatFilterNudge } from "./ChatFilterNudge";
import { FilterCheckboxList } from "./FilterCheckboxList";

const FILTER_BY_LABEL = "Filter by";
const ENTITIES_SECTION = "ENTITIES";
const STATUS_SECTION = "STATUS";
const YEAR_SECTION = "YEAR";

const StatusFilter = () => {
  const {
    isUnreadOnly,
    isPendingActionOnly,
    setUnreadOnly,
    setPendingActionOnly,
  } = useEntityChatFilter();

  const handleUnreadChange = () => setUnreadOnly(!isUnreadOnly);

  const handlePendingActionChange = () =>
    setPendingActionOnly(!isPendingActionOnly);

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Checkbox
        checked={isUnreadOnly}
        label="Unread chat"
        name="unreadChat"
        onChange={handleUnreadChange}
      />
      <Checkbox
        checked={isPendingActionOnly}
        label="Chat with pending actions"
        name="pendingActionsChat"
        onChange={handlePendingActionChange}
      />
    </div>
  );
};

const EntitiesFilter = () => {
  const entities = useUsEntities();
  const { selectedEntityIds, setSelectedEntityIds } = useEntityChatFilter();

  return (
    <FilterCheckboxList
      allLabel="All Entities"
      onChange={setSelectedEntityIds}
      options={entities.map(({ uuid, name }) => ({ value: uuid, label: name }))}
      selected={selectedEntityIds}
    />
  );
};

const YearFilter = ({ seasons }: { seasons: string[] }) => {
  const { selectedSeasons, setSelectedSeasons } = useEntityChatFilter();

  return (
    <FilterCheckboxList
      allLabel="All Years"
      onChange={setSelectedSeasons}
      options={seasons.map((season) => ({ value: season, label: season }))}
      selected={selectedSeasons}
    />
  );
};

export const ChatGroupFilters = () => {
  const seasons = useChatSeasons();
  const { markSeen } = useChatFilterNudge();

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      void markSeen();
    }
  };

  return (
    <Filter.Root
      defaultValue={ENTITIES_SECTION}
      onOpenChange={handleOpenChange}
      trigger={
        <ChatFilterNudge>
          <Button size="xsmall">
            <span className="t-flex t-items-center t-gap-1 t-text-body-sm">
              {FILTER_BY_LABEL}
              <ArrowDown strokeWidth="1.5" />
            </span>
          </Button>
        </ChatFilterNudge>
      }
    >
      <Filter.Portal size="small">
        <Filter.List size="small">
          <Filter.ListItem value={ENTITIES_SECTION}>Entities</Filter.ListItem>
          <Filter.ListItem value={STATUS_SECTION}>Status</Filter.ListItem>
          {seasons.length > 0 && (
            <Filter.ListItem value={YEAR_SECTION}>Year</Filter.ListItem>
          )}
        </Filter.List>
        <Filter.Body block value={ENTITIES_SECTION}>
          <EntitiesFilter />
        </Filter.Body>
        <Filter.Body block value={STATUS_SECTION}>
          <StatusFilter />
        </Filter.Body>
        <Filter.Body block value={YEAR_SECTION}>
          <YearFilter seasons={seasons} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
